import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AlertTriangle, Filter, RefreshCw, Search, X } from 'lucide-react';
import {
  IncidentTable,
  IncidentQueueCardList,
  IncidentSidebar,
  BulkActionToolbar,
  BulkCloseModal,
  type QuickFilter,
} from '../components/incidents';
import { cn } from '../lib/utils';
import { SkeletonTable } from '../components/ui/Skeleton';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import {
  getIncidents,
  getIncidentCounts,
  getTenants,
  bulkAssignIncidents,
  bulkLinkIncidents,
} from '../services';
import { useUser } from '../context/UserContext';
import { usePolling } from '../hooks/usePolling';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useIncidentListState, describeActiveFilters, PAGE_SIZES } from '../hooks/useIncidentListState';
import type { EnrichedIncident, PlatformStatus, Tenant, User } from '../data/types';

const POLL_INTERVAL_MS = 30_000;

// ============================================================================
// HELPERS
// ============================================================================

// Generate unique key for an incident
function getIncidentKey(incident: EnrichedIncident): string {
  return `${incident.tenantId}:${incident.incidentId}`;
}

// Get incidents from selected IDs
function getSelectedIncidents(
  incidents: EnrichedIncident[],
  selectedIds: Set<string>
): EnrichedIncident[] {
  return incidents.filter((inc) => selectedIds.has(getIncidentKey(inc)));
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function IncidentList() {
  const { currentUser, availableUsers } = useUser();
  const isMobile = useIsMobile();

  // Filters + paging live in the URL (and are remembered per tab) — see the hook.
  const {
    state: listState,
    key: listKey,
    setSearch,
    setQuickFilter,
    setTenants: setSelectedTenants,
    setSeverities: setSelectedSeverities,
    setStatuses: setSelectedStatuses,
    setDateFrom,
    setDateTo,
    setHideAutoResolved,
    setPage,
    setPageSize,
    clear: clearListState,
  } = useIncidentListState();
  const {
    search: searchQuery,
    quick: activeQuickFilter,
    tenants: selectedTenants,
    severities: selectedSeverities,
    statuses: selectedStatuses,
    dateFrom,
    dateTo,
    hideAutoResolved,
    page,
    pageSize,
  } = listState;

  // Data state
  const [incidents, setIncidents] = useState<EnrichedIncident[]>([]);
  const [total, setTotal] = useState(0);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [counts, setCounts] = useState<{
    allOpen?: number;
    myTickets?: number;
    unassigned?: number;
    closed?: number;
  }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [showBulkCloseModal, setShowBulkCloseModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showBulkLinkModal, setShowBulkLinkModal] = useState(false);
  const [bulkLinkName, setBulkLinkName] = useState('');

  // Compute effective filters based on quick filter selection
  const effectiveFilters = useMemo(() => {
    const base = {
      search: searchQuery || undefined,
      tenants: selectedTenants.length > 0 ? selectedTenants : undefined,
      severities: selectedSeverities.length > 0 ? selectedSeverities : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      excludeAutoResolved: hideAutoResolved || undefined,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    };

    switch (activeQuickFilter) {
      case 'all_open':
        return {
          ...base,
          statuses: ['new', 'acknowledged', 'investigating', 'reopened'] as PlatformStatus[],
        };
      case 'my_tickets':
        return {
          ...base,
          statuses: ['new', 'acknowledged', 'investigating', 'reopened'] as PlatformStatus[],
          assignedTo: currentUser?.userId,
        };
      case 'unassigned':
        return {
          ...base,
          statuses: ['new', 'acknowledged', 'investigating', 'reopened'] as PlatformStatus[],
          assignedTo: null,
        };
      case 'closed':
        return {
          ...base,
          statuses: ['closed'] as PlatformStatus[],
        };
      default:
        return {
          ...base,
          statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        };
    }
  }, [
    activeQuickFilter,
    searchQuery,
    selectedTenants,
    selectedSeverities,
    selectedStatuses,
    dateFrom,
    dateTo,
    hideAutoResolved,
    page,
    pageSize,
    currentUser?.userId,
  ]);

  // Polling state
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const hasLoadedOnce = useRef(false);
  // Monotonic request counter: a slow older response must not overwrite a
  // newer page (page clicks make this race visible immediately).
  const requestSeq = useRef(0);

  // Core data fetcher — `silent` skips the loading skeleton (used by polling)
  const fetchData = useCallback(async (silent = false) => {
    const seq = ++requestSeq.current;
    if (!silent) {
      setIsLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);

    try {
      const [incidentsResult, tenantsResult, countsResult] = await Promise.all([
        getIncidents(effectiveFilters),
        getTenants(),
        getIncidentCounts(currentUser?.userId),
      ]);
      if (seq !== requestSeq.current) return; // superseded

      if (incidentsResult.success) {
        setIncidents(incidentsResult.data);
        setTotal(incidentsResult.pagination?.total ?? incidentsResult.data.length);
      } else if (!silent) {
        setError(incidentsResult.error || 'Failed to load incidents');
      }

      if (tenantsResult.success) {
        setTenants(tenantsResult.data);
      }

      if (countsResult.success) {
        setCounts(countsResult.data);
      }

      setLastUpdated(new Date());
    } catch (err) {
      if (seq !== requestSeq.current) return;
      if (!silent) {
        setError('Failed to connect to API');
      }
      console.error('Error fetching data:', err);
    } finally {
      if (seq === requestSeq.current) {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [effectiveFilters, currentUser?.userId]);

  // Initial load + reload on filter/page changes (skeleton on first load only)
  useEffect(() => {
    const isFirstLoad = !hasLoadedOnce.current;
    hasLoadedOnce.current = true;
    fetchData(!isFirstLoad);
  }, [fetchData]);

  // Silent background polling every 30 s
  const silentRefresh = useCallback(() => fetchData(true), [fetchData]);
  const { reset: resetPollTimer } = usePolling(silentRefresh, POLL_INTERVAL_MS);

  // Manual refresh — full fetch + reset poll timer
  const handleManualRefresh = useCallback(() => {
    fetchData(false);
    resetPollTimer();
  }, [fetchData, resetPollTimer]);

  // Clear selection when the view (filters, page, page size) changes
  useEffect(() => {
    setSelectedIds(new Set());
  }, [listKey]);

  // If the result set shrinks under us (polling, bulk close) and the current
  // page is now past the end, fall back to the last real page.
  useEffect(() => {
    if (isLoading || total === 0 || incidents.length > 0 || page <= 1) return;
    setPage(Math.max(1, Math.ceil(total / pageSize)));
  }, [isLoading, total, incidents.length, page, pageSize, setPage]);

  // No bulk selection on mobile — cards have no checkboxes
  useEffect(() => {
    if (isMobile) {
      setSelectedIds(new Set());
    }
  }, [isMobile]);

  // Replace a single row in place (e.g. after a quick acknowledge on mobile)
  const handleIncidentUpdated = useCallback(
    (updated: EnrichedIncident) => {
      setIncidents((prev) =>
        prev.map((inc) => (getIncidentKey(inc) === getIncidentKey(updated) ? updated : inc))
      );
      resetPollTimer();
    },
    [resetPollTimer]
  );

  const tenantOptions = tenants.map((t) => ({
    tenantId: t.tenantId,
    displayName: t.displayName,
  }));

  // Chips for every filter narrowing the view — rendered above the table so a
  // filtered list can never pass for the whole queue (AB#137).
  const activeFilterChips = useMemo(
    () =>
      describeActiveFilters(
        listState,
        Object.fromEntries(tenants.map((t) => [t.tenantId, t.displayName]))
      ),
    [listState, tenants]
  );

  const selectedIncidents = useMemo(
    () => getSelectedIncidents(incidents, selectedIds),
    [incidents, selectedIds]
  );

  // Bulk action handlers — use full fetch + reset poll timer
  const handleBulkCloseComplete = async () => {
    await fetchData(false);
    resetPollTimer();
    setSelectedIds(new Set());
  };

  const handleBulkAssign = async (userId: string | null) => {
    const result = await bulkAssignIncidents({
      incidents: selectedIncidents.map((inc) => ({
        tenantId: inc.tenantId,
        incidentId: inc.incidentId,
      })),
      userId,
    });

    if (result.success) {
      await fetchData(false);
      resetPollTimer();
      setSelectedIds(new Set());
      setShowBulkAssignModal(false);
    }
  };

  const handleBulkLink = async () => {
    if (!bulkLinkName.trim()) return;

    const result = await bulkLinkIncidents({
      incidents: selectedIncidents.map((inc) => ({
        tenantId: inc.tenantId,
        incidentId: inc.incidentId,
      })),
      linkGroupName: bulkLinkName.trim(),
    });

    if (result.success) {
      await fetchData(false);
      resetPollTimer();
      setSelectedIds(new Set());
      setShowBulkLinkModal(false);
      setBulkLinkName('');
    }
  };

  return (
    <div className="flex h-full animate-fade-in">
      {/* Sidebar (desktop filter panel) */}
      <IncidentSidebar
        className="hidden md:flex"
        searchQuery={searchQuery}
        onSearchChange={setSearch}
        activeQuickFilter={activeQuickFilter}
        onQuickFilterChange={setQuickFilter}
        selectedSeverities={selectedSeverities}
        onSeverityChange={setSelectedSeverities}
        selectedStatuses={selectedStatuses}
        onStatusChange={setSelectedStatuses}
        selectedTenants={selectedTenants}
        onTenantChange={setSelectedTenants}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        hideAutoResolved={hideAutoResolved}
        onHideAutoResolvedChange={setHideAutoResolved}
        tenants={tenantOptions}
        currentUserId={currentUser?.userId}
        counts={counts}
        onClearAll={clearListState}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4 border-b border-border-default bg-bg-primary">
          <div>
            <h1 className="text-xl font-semibold text-text-primary flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Incidents
            </h1>
            <p className="text-sm text-text-secondary mt-0.5">
              {isLoading
                ? 'Loading...'
                : `${total} ${activeFilterChips.length > 0 ? 'matching ' : ''}incident${total !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && !isLoading && (
              <span className="flex items-center gap-1.5 text-xs text-text-muted">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
                </span>
                Live
              </span>
            )}
            <Button variant="ghost" size="sm" onClick={handleManualRefresh} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Active-filter banner: visible on every viewport, above the table */}
        {activeFilterChips.length > 0 && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-2 px-4 md:px-6 py-2 border-b border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-900 dark:text-amber-200"
          >
            <Filter className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="font-medium">Filtered view</span>
            {activeFilterChips.map((chip) => (
              <span
                key={chip.key}
                className="px-2 py-0.5 rounded-full bg-white/70 dark:bg-black/20 border border-amber-200 dark:border-amber-700 truncate max-w-[16rem]"
                title={chip.label}
              >
                {chip.label}
              </span>
            ))}
            <button
              type="button"
              onClick={clearListState}
              className="ml-auto inline-flex items-center gap-1 min-h-8 px-2 rounded font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40"
            >
              <X className="w-3.5 h-3.5" />
              Clear filters
            </button>
          </div>
        )}

        {/* Mobile search + quick filter chips */}
        <div className="md:hidden px-4 pt-3 pb-1 space-y-3 border-b border-border-default bg-bg-primary">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search incidents..."
              className="w-full min-h-11 pl-9 pr-3 bg-bg-tertiary border border-border-default rounded-lg text-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
            {(
              [
                { id: 'all_open', label: 'All Open', count: counts.allOpen },
                { id: 'my_tickets', label: 'My Tickets', count: counts.myTickets },
                { id: 'unassigned', label: 'Unassigned', count: counts.unassigned },
                { id: 'closed', label: 'Closed', count: counts.closed },
              ] as { id: QuickFilter; label: string; count?: number }[]
            ).map(({ id, label, count }) => (
              <button
                key={id}
                type="button"
                onClick={() => setQuickFilter(id)}
                className={cn(
                  'shrink-0 inline-flex items-center gap-1.5 px-3 min-h-11 rounded-full text-sm font-medium border transition-colors',
                  activeQuickFilter === id
                    ? 'bg-brand-blue text-white border-brand-blue'
                    : 'bg-bg-secondary text-text-secondary border-border-default'
                )}
              >
                {label}
                {count !== undefined && (
                  <span
                    className={cn(
                      'text-xs font-mono',
                      activeQuickFilter === id ? 'text-white/80' : 'text-text-muted'
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-4 md:mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* Table (desktop) / card list (mobile) */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          {isLoading ? (
            isMobile ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-32 rounded-xl bg-bg-secondary border border-border-default animate-pulse"
                  />
                ))}
              </div>
            ) : (
              <div className="bg-bg-secondary rounded-xl border border-border-default overflow-hidden">
                <SkeletonTable rows={10} columns={8} />
              </div>
            )
          ) : incidents.length > 0 ? (
            isMobile ? (
              <IncidentQueueCardList
                incidents={incidents}
                onIncidentUpdated={handleIncidentUpdated}
              />
            ) : (
              <div className="bg-bg-secondary rounded-xl border border-border-default overflow-hidden">
                <IncidentTable
                  incidents={incidents}
                  selectable
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                />
              </div>
            )
          ) : (
            <div className="bg-bg-secondary rounded-xl border border-border-default p-12 text-center">
              <AlertTriangle className="w-12 h-12 text-text-muted mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">
                No incidents found
              </h3>
              <p className="text-text-secondary">
                Try adjusting your filters or search query
              </p>
            </div>
          )}
        </div>

        {/* Pagination footer (kept clear of the fixed bulk toolbar while rows are selected) */}
        {!isLoading && total > 0 && (
          <div
            className={cn(
              'px-4 md:px-6 py-2 border-t border-border-default bg-bg-primary',
              selectedIds.size > 0 && 'pb-24'
            )}
          >
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={PAGE_SIZES}
              showPageNumbers={!isMobile}
              disabled={isRefreshing}
            />
          </div>
        )}
      </div>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={selectedIds.size}
        selectedIncidents={selectedIncidents}
        onClearSelection={() => setSelectedIds(new Set())}
        onBulkClose={() => setShowBulkCloseModal(true)}
        onBulkLink={() => setShowBulkLinkModal(true)}
        onBulkAssign={() => setShowBulkAssignModal(true)}
      />

      {/* Bulk Close Modal */}
      <BulkCloseModal
        isOpen={showBulkCloseModal}
        onClose={() => setShowBulkCloseModal(false)}
        selectedIncidents={selectedIncidents}
        onComplete={handleBulkCloseComplete}
      />

      {/* Bulk Assign Modal */}
      <Modal
        isOpen={showBulkAssignModal}
        onClose={() => setShowBulkAssignModal(false)}
        title={`Assign ${selectedIds.size} Incident${selectedIds.size !== 1 ? 's' : ''}`}
        description="Select a user to assign these incidents to"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowBulkAssignModal(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => handleBulkAssign(null)}
            className="w-full p-3 text-left rounded-lg border border-border-default hover:bg-bg-tertiary transition-colors"
          >
            <span className="text-sm font-medium text-text-secondary">
              Unassign
            </span>
            <p className="text-xs text-text-secondary mt-0.5">
              Remove current assignment
            </p>
          </button>
          {availableUsers.map((user: User) => (
            <button
              key={user.userId}
              type="button"
              onClick={() => handleBulkAssign(user.userId)}
              className="w-full p-3 text-left rounded-lg border border-border-default hover:bg-bg-tertiary transition-colors"
            >
              <span className="text-sm font-medium text-text-secondary">
                {user.displayName}
              </span>
              <p className="text-xs text-text-secondary mt-0.5">{user.email}</p>
            </button>
          ))}
        </div>
      </Modal>

      {/* Bulk Link Modal */}
      <Modal
        isOpen={showBulkLinkModal}
        onClose={() => {
          setShowBulkLinkModal(false);
          setBulkLinkName('');
        }}
        title={`Link ${selectedIds.size} Incident${selectedIds.size !== 1 ? 's' : ''}`}
        description="Create a relationship between these incidents"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setShowBulkLinkModal(false);
                setBulkLinkName('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkLink} disabled={!bulkLinkName.trim()}>
              Link Incidents
            </Button>
          </div>
        }
      >
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            Group Name
          </label>
          <input
            type="text"
            value={bulkLinkName}
            onChange={(e) => setBulkLinkName(e.target.value)}
            placeholder="e.g., Phishing Campaign Jan 2026"
            className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue"
          />
          <p className="mt-2 text-xs text-text-secondary">
            This will group these incidents together for tracking and reporting purposes.
          </p>
        </div>
      </Modal>
    </div>
  );
}
